import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface WeeklyReview {
  id: string;
  user_id: string;
  week_start: string;
  week_end: string;
  vents_posted: number;
  comments_posted: number;
  likes_given: number;
  vent_likes_received: number;
  comment_likes_received: number;
  comments_received: number;
  wellness_total: number;
  wellness_breakdown: {
    journal?: number;
    self_care?: number;
    coping?: number;
    tools_used?: number;
    tools_total?: number;
    top_vent_likes?: number;
    top_comment_likes?: number;
  } | null;
  badges_earned: number;
  viewed_at: string | null;
  dismissed_at: string | null;
  created_at: string;
}

const SELECT =
  'id, user_id, week_start, week_end, vents_posted, comments_posted, likes_given, vent_likes_received, comment_likes_received, comments_received, wellness_total, wellness_breakdown, badges_earned, viewed_at, dismissed_at, created_at';

export function useWeeklyReview() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<WeeklyReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setReviews([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: loadError } = await supabase
      .from('user_weekly_reviews')
      .select(SELECT)
      .eq('user_id', user.id)
      .order('week_start', { ascending: false })
      .limit(26);

    if (loadError) {
      console.error('[useWeeklyReview]', loadError.message);
      setReviews([]);
      setError(loadError.message);
    } else {
      setReviews((data ?? []) as unknown as WeeklyReview[]);
      setError(null);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const markViewed = useCallback(async (id: string) => {
    setReviews(prev => prev.map(r => (r.id === id ? { ...r, viewed_at: r.viewed_at ?? new Date().toISOString() } : r)));
    await supabase.from('user_weekly_reviews').update({ viewed_at: new Date().toISOString() }).eq('id', id).is('viewed_at', null);
  }, []);

  const markDismissed = useCallback(async (id: string) => {
    const now = new Date().toISOString();
    setReviews(prev => prev.map(r => (r.id === id ? { ...r, dismissed_at: now, viewed_at: r.viewed_at ?? now } : r)));
    await supabase.from('user_weekly_reviews').update({ dismissed_at: now, viewed_at: now }).eq('id', id);
  }, []);

  const latest = reviews[0] ?? null;
  const pending = latest && !latest.dismissed_at && !latest.viewed_at ? latest : null;

  return { reviews, latest, pending, loading, error, refresh: load, markViewed, markDismissed };
}

export function formatWeekRange(weekStart: string, weekEnd: string): string {
  const fmt = (key: string) => {
    const [y, m, d] = key.split('-').map(Number);
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
      new Date(Date.UTC(y, m - 1, d)),
    );
  };
  return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
}
